import Api from "@/api"
import { useLoading } from "@/composition"
import {
	MarriageClaimExport,
	MarriageStageType,
	MarriageStatus,
	type MarriageClaim,
	type MarriageClaimInitialData,
	type MarriageSignatureVerification,
	type TaxYearBracket,
} from "@/models"
import { type InjectionKey, type Reactive } from "vue"
import { useService } from "vue-utils"

export const EditingMarriageClaimInjectionKey: InjectionKey<EditingMarriageClaimState> =
	Symbol("EditingMarriageClaimState")

export function useEditingMarriageClaimState() {
	return useService(EditingMarriageClaimInjectionKey)
}

export class EditingMarriageClaimState {
	private runAction: <T>(promise: PromiseLike<T>) => Promise<T>
	private claim: Reactive<MarriageClaim>
	private exports: Reactive<MarriageClaimExport[]>
	private taxBracketsByYear: Map<number, TaxYearBracket> = new Map()
	private signatureData: MarriageSignatureVerification
	private partnerSignatureData: MarriageSignatureVerification

	constructor(data: MarriageClaimInitialData) {
		const { runAction } = useLoading()
		this.runAction = runAction
		this.claim = data.claim
		this.exports = data.exports
		this.signatureData = data.signatureData
		this.partnerSignatureData = data.partnerSignatureData

		for (const bracket of data.taxYearBrackets) {
			this.taxBracketsByYear.set(bracket.Year, bracket)
		}
	}

	getTaxYearBracket(year: number): TaxYearBracket {
		const bracket = this.taxBracketsByYear.get(year)
		if (bracket === undefined) {
			throw new Error(`Could not find bracket for year: ${year}`)
		} else {
			return bracket
		}
	}

	get fullClaim() {
		return this.claim
	}

	get allExports() {
		return this.exports
	}

	get getSignatureData() {
		return this.signatureData
	}

	get getPartnerSignatureData() {
		return this.partnerSignatureData
	}

	async updateClaimStatus(newStatus: MarriageStatus) {
		this.claim.Status = newStatus
		Object.assign(this.claim, await this.runAction(Api.marriageClaims.updateClaim(this.claim)))
	}

	async refreshSignatureDetails(type: MarriageStageType) {
		Object.assign(
			type === MarriageStageType.P1 ? this.signatureData : this.partnerSignatureData,
			await Api.marriageClaims.getSignatureVerificationStatus(this.claim.Id, type),
			1000
		)
	}
}
